import type { SdkPollInterval } from '@Pages/pageRuntime/sdk/types';
import { focusManager } from '@tanstack/react-query';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

/**
 * Shortest interval a page may poll on. Anything faster is a mistake rather than a
 * requirement: every `usePoll` tick re-renders the page server-side, and every
 * `pollInterval` tick is a record API request.
 */
export const MINIMUM_POLL_INTERVAL_MS = 1000;

/** The subset of TanStack's `Query` that a `refetchInterval` callback reads. */
type QueryLike<TQueryData> = { state: { data: TQueryData | undefined } };

export type RefetchInterval<TQueryData> =
  | number
  | false
  | ((query: QueryLike<TQueryData>) => number | false)
  | undefined;

/**
 * Clamp an author-supplied interval to {@link MINIMUM_POLL_INTERVAL_MS}, warning at most
 * once per hook instance. The function form of `pollInterval` is re-evaluated on every
 * tick, so an unguarded warning would fill the console.
 */
export function useClampedPollInterval(): (interval: number) => number {
  const hasWarned = useRef(false);

  return (interval: number) => {
    if (interval >= MINIMUM_POLL_INTERVAL_MS) {
      return interval;
    }

    if (!hasWarned.current) {
      hasWarned.current = true;
      // oxlint-disable-next-line no-console -- Page authors must see that their interval was raised.
      console.warn(
        `Poll interval ${interval}ms is below the ${MINIMUM_POLL_INTERVAL_MS}ms minimum and was raised to ${MINIMUM_POLL_INTERVAL_MS}ms.`
      );
    }

    return MINIMUM_POLL_INTERVAL_MS;
  };
}

/** What an author's `pollInterval` resolved to, after clamping. */
type ResolvedPollInterval = number | false | undefined;

type ResolvedPollIntervalStore = {
  getSnapshot: () => ResolvedPollInterval;
  set: (interval: ResolvedPollInterval) => void;
  subscribe: (onStoreChange: () => void) => () => void;
};

function createResolvedPollIntervalStore(): ResolvedPollIntervalStore {
  let interval: ResolvedPollInterval;
  const listeners = new Set<() => void>();

  return {
    getSnapshot: () => interval,
    set: (nextInterval) => {
      if (interval === nextInterval) {
        return;
      }

      interval = nextInterval;

      for (const listener of listeners) {
        listener();
      }
    },
    subscribe: (onStoreChange) => {
      listeners.add(onStoreChange);

      return () => listeners.delete(onStoreChange);
    }
  };
}

/**
 * What {@link usePollRefetchInterval} hands back: the value TanStack polls on, plus what
 * that value resolved to, so {@link usePollCountdown} can count down to the next request
 * without asking the author's callback a second time. A second call would double their
 * work and could disagree with the schedule TanStack is running.
 *
 * `fixedInterval` holds the clamped interval of an author-supplied number. It is a plain
 * value, so the countdown can depend on it and restart when the author changes it.
 * `dynamicInterval` holds what the function form last resolved to and notifies the countdown
 * when that schedule changes. Both are `null` when that form was not used.
 */
export type PollSchedule<TQueryData> = {
  refetchInterval: RefetchInterval<TQueryData>;
  fixedInterval: number | null;
  dynamicInterval: ResolvedPollIntervalStore | null;
};

/**
 * Translate the SDK's `pollInterval` option into TanStack Query's `refetchInterval`.
 * `selectData` maps the cached query data onto the value the hook itself returns, so the
 * author's callback sees the same `data` shape it renders. The shape, not always the
 * value: under `placeholderData: keepPreviousData` the hook renders the previous key's
 * data while the callback reads the new key's still-empty cache, so a "stop when data
 * arrives" callback re-polls for one cycle after a page or filter change.
 */
export function usePollRefetchInterval<TQueryData, TData>(
  pollInterval: SdkPollInterval<TData> | undefined,
  selectData: (queryData: TQueryData | undefined) => TData
): PollSchedule<TQueryData> {
  const clamp = useClampedPollInterval();
  const [dynamicInterval] = useState(createResolvedPollIntervalStore);

  if (typeof pollInterval === 'function') {
    return {
      dynamicInterval,
      fixedInterval: null,
      refetchInterval: (query) => {
        const next = pollInterval(selectData(query.state.data));
        const resolved = next === false ? false : clamp(next);

        dynamicInterval.set(resolved);

        return resolved;
      }
    };
  }

  const resolved = typeof pollInterval === 'number' ? clamp(pollInterval) : pollInterval;

  return {
    dynamicInterval: null,
    refetchInterval: resolved,
    fixedInterval: typeof resolved === 'number' ? resolved : null
  };
}

/** The query state a countdown reads. TanStack's `useQuery` result satisfies it. */
type CountdownQuery = {
  isFetching: boolean;
  dataUpdatedAt: number;
  errorUpdatedAt: number;
};

/** The deadline the countdown is running against, and what produced it. */
type CountdownDeadline = { interval: number; startedAt: number; at: number };

/** When the interval TanStack currently polls on took effect. */
type IntervalStart = { value: number; startedAt: number };

const subscribeToFocus = (onStoreChange: () => void) => focusManager.subscribe(onStoreChange);
const getIsTabVisible = () => focusManager.isFocused();
/** A hook that is not counting down must not re-render when the tab changes. */
const ignoreFocus = () => () => {};
const alwaysVisible = () => true;
const getNoPollInterval = () => undefined;

/**
 * Seconds until `query` refetches itself on its poll interval, or `null` when no refresh
 * is scheduled.
 *
 * The deadline is absolute rather than a decrementing counter, so a throttled or delayed
 * timer callback still reports the truth. TanStack restarts its `refetchInterval` timer on
 * whichever happened last: the query settling — successfully or not — or the interval it
 * polls on taking effect. The countdown anchors on the later of the two, so switching
 * polling on over an already-loaded record counts a full interval from the switch rather
 * than from a settle that may be minutes old. An unrelated re-render changes neither
 * anchor and leaves the deadline standing.
 *
 * A running countdown re-renders its page every second. A query with no `pollInterval`
 * never starts one, and reads no query state either, so it keeps the render behaviour it
 * had before the countdown existed.
 */
export function usePollCountdown<TQueryData>(
  schedule: PollSchedule<TQueryData>,
  query: CountdownQuery,
  enabled: boolean
): number | null {
  const { fixedInterval, dynamicInterval } = schedule;
  const [refreshIn, setRefreshIn] = useState<number | null>(null);
  const deadlineRef = useRef<CountdownDeadline | null>(null);
  const intervalStartRef = useRef<IntervalStart | null>(null);
  const resolvedDynamicInterval = useSyncExternalStore(
    dynamicInterval?.subscribe ?? ignoreFocus,
    dynamicInterval?.getSnapshot ?? getNoPollInterval,
    getNoPollInterval
  );

  const isPolling = enabled && (fixedInterval !== null || dynamicInterval !== null);

  // Reading a query field subscribes the page to that field's changes, so hold off unless
  // the countdown needs it.
  const isFetching = isPolling && query.isFetching;
  const settledAt = isPolling ? Math.max(query.dataUpdatedAt, query.errorUpdatedAt) : 0;
  const isTabVisible = useSyncExternalStore(
    isPolling ? subscribeToFocus : ignoreFocus,
    isPolling ? getIsTabVisible : alwaysVisible,
    alwaysVisible
  );

  useEffect(() => {
    const interval = fixedInterval ?? resolvedDynamicInterval;

    if (typeof interval !== 'number') {
      intervalStartRef.current = null;
    } else if (intervalStartRef.current?.value !== interval) {
      // TanStack replaces its timer the moment the interval it polls on changes, so record
      // when this value took effect. A poll that is not running yet still records: an
      // interval that predates the first settle must not push the deadline past it.
      intervalStartRef.current = { value: interval, startedAt: Date.now() };
    }

    if (!isPolling || settledAt === 0 || typeof interval !== 'number') {
      deadlineRef.current = null;
      setRefreshIn(null);

      return;
    }

    if (!isTabVisible) {
      // TanStack keeps ticking while the tab is hidden but sends no request, so the
      // schedule survives untouched and only the countdown is withheld.
      setRefreshIn(null);

      return;
    }

    if (isFetching) {
      setRefreshIn(0);

      return;
    }

    const now = Date.now();
    // Whichever came last restarted TanStack's timer. Anchoring on the settle alone would
    // count from a stale load when polling is switched on later; anchoring on this effect
    // alone would drift by however long the settle took to reach it.
    const startedAt = Math.max(settledAt, intervalStartRef.current?.startedAt ?? 0);
    const previous = deadlineRef.current;
    let deadline: CountdownDeadline;

    if (previous === null || previous.startedAt !== startedAt || previous.interval !== interval) {
      deadline = { interval, startedAt, at: startedAt + interval };
    } else {
      deadline = previous;
    }

    // A deadline can pass while the tab is hidden: TanStack skips those requests but keeps
    // the same cadence, so step forward whole intervals instead of starting a fresh one.
    while (deadline.at <= now) {
      deadline.at += interval;
    }

    deadlineRef.current = deadline;

    const at = deadline.at;
    const tick = () => setRefreshIn(Math.max(0, Math.ceil((at - Date.now()) / 1000)));

    tick();

    const timer = setInterval(tick, 1000);

    return () => clearInterval(timer);
  }, [fixedInterval, isFetching, isPolling, isTabVisible, resolvedDynamicInterval, settledAt]);

  return refreshIn;
}
